import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Users } from "lucide-react";
import { toast } from "sonner";
import { employeesAPI } from "../services/api";
import ImportUploadStep from "./import/ImportUploadStep";
import ImportMappingStep from "./import/ImportMappingStep";
import ImportPreviewStep from "./import/ImportPreviewStep";
import ImportResultStep from "./import/ImportResultStep";
import ImportFooter from "./import/ImportFooter";

const ImportEmployeesModal = ({ open, onClose, onImportSuccess }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState("upload");
  const [selectedFile, setSelectedFile] = useState(null);
  const [detectionResult, setDetectionResult] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const handleClose = () => {
    setStep("upload");
    setSelectedFile(null);
    setDetectionResult(null);
    setColumnMapping({});
    setValidationResult(null);
    setImportResult(null);
    onClose();
  };

  const handleDownloadTemplate = async () => {
    try {
      await employeesAPI.downloadTemplate();
      toast.success(t("importEmployees.toasts.templateDownloaded"), {
        description: t("importEmployees.toasts.templateDownloadedDesc"),
      });
    } catch {
      toast.error(t("importEmployees.toasts.downloadError"), {
        description: t("importEmployees.toasts.downloadErrorDesc"),
      });
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const filename = file.name.toLowerCase();
    if (!filename.endsWith(".csv") && !filename.endsWith(".xlsx") && !filename.endsWith(".xls")) {
      toast.error(t("importEmployees.toasts.unsupportedFormat"), {
        description: t("importEmployees.toasts.unsupportedFormatDesc"),
      });
      return;
    }

    setSelectedFile(file);
    setIsDetecting(true);
    try {
      const result = await employeesAPI.detectColumns(file);
      setDetectionResult(result);
      setColumnMapping(result.auto_mapping || {});
      setStep("mapping");
      toast.success(t("importEmployees.toasts.columnsDetected"), {
        description: t("importEmployees.toasts.columnsDetectedDesc", { count: result.detected_columns.length }),
      });
    } catch (error) {
      toast.error(t("importEmployees.toasts.detectionError"), {
        description: error.response?.data?.detail || t("importEmployees.toasts.detectionErrorDesc"),
      });
    } finally {
      setIsDetecting(false);
    }
  };

  const handleValidateWithMapping = async () => {
    if (!selectedFile) return;
    setIsValidating(true);
    try {
      const result = await employeesAPI.validateImportFile(selectedFile, columnMapping);
      setValidationResult(result);
      setStep("preview");
      if (result.can_import) {
        toast.success(t("importEmployees.toasts.validFile"), {
          description: t("importEmployees.toasts.validFileDesc", { count: result.valid_lines }),
        });
      } else {
        toast.warning(t("importEmployees.toasts.errorsDetected"), {
          description: t("importEmployees.toasts.errorsDetectedDesc", { count: result.error_lines }),
        });
      }
    } catch (error) {
      toast.error(t("importEmployees.toasts.validationError"), {
        description: error.response?.data?.detail || t("importEmployees.toasts.validationErrorDesc"),
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    setIsImporting(true);
    try {
      const result = await employeesAPI.importEmployees(selectedFile, columnMapping);
      setImportResult(result);
      setStep("result");
      toast.success(t("importEmployees.toasts.importComplete"), {
        description: t("importEmployees.toasts.importCompleteDesc", { count: result.imported }),
        duration: 5000,
      });
      if (onImportSuccess) onImportSuccess();
    } catch (error) {
      toast.error(t("importEmployees.toasts.importError"), {
        description: error.response?.data?.detail || t("importEmployees.toasts.importErrorDesc"),
        duration: 8000,
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>{t("importEmployees.title")}</span>
          </DialogTitle>
          <DialogDescription>
            {t("importEmployees.description")}
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <ImportUploadStep
            handleDownloadTemplate={handleDownloadTemplate}
            handleFileSelect={handleFileSelect}
            isDetecting={isDetecting}
          />
        )}
        {step === "mapping" && detectionResult && (
          <ImportMappingStep
            detectionResult={detectionResult}
            columnMapping={columnMapping}
            setColumnMapping={setColumnMapping}
          />
        )}
        {step === "preview" && validationResult && (
          <ImportPreviewStep validationResult={validationResult} />
        )}
        {step === "result" && importResult && (
          <ImportResultStep importResult={importResult} />
        )}

        <ImportFooter
          step={step}
          setStep={setStep}
          handleClose={handleClose}
          handleValidateWithMapping={handleValidateWithMapping}
          handleImport={handleImport}
          isValidating={isValidating}
          isImporting={isImporting}
          detectionResult={detectionResult}
          columnMapping={columnMapping}
          validationResult={validationResult}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ImportEmployeesModal;
